import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, NumberEditor } from '@approvalmax/ui';
import { constants, statics } from 'modules/common';
import { domain } from 'modules/data';
import { FC, memo, useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch, useSelector } from 'react-redux';
import { useGetStandaloneRequestCurrencies } from 'shared/data/webApp/v2';

import { changeStandaloneAmount, changeStandaloneCurrency, changeStandaloneDate } from '../../../actions';
import { getRequest, getShowValidationErrors } from '../../../selectors/pageSelectors';
import RequestCurrenciesDataProvider from '../../xero/dataProviders/RequestCurrenciesDataProvider/RequestCurrenciesDataProvider';
import { messages } from './StandaloneRequestFieldsSection.messages';
import { Root, StyledField } from './StandaloneRequestFieldsSection.styles';

const { standaloneConstants } = constants;

const qa = bemFactory.qa('reqf-standalone-request-fields-section');

const StandaloneRequestFieldsSection: FC = memo(() => {
    const dispatch = useDispatch();
    const request = useSelector(getRequest<domain.StandaloneRequest>);
    const showValidationErrors = useSelector(getShowValidationErrors);
    const { amount, currency, companyId, id: requestId } = request;
    const { date } = request.details;
    const { data } = useGetStandaloneRequestCurrencies({
        path: {
            companyId,
            requestId,
        },
    });

    const isCrypto = useMemo(() => {
        const currentCurrency = data?.currencies.find(({ code }) => code === currency);

        return currentCurrency?.isCrypto ?? false;
    }, [data?.currencies, currency]);

    const isInvalidAmount =
        showValidationErrors &&
        (amount === null ||
            amount > standaloneConstants.TOTAL_MAX_AMOUNT ||
            amount < standaloneConstants.TOTAL_MIN_AMOUNT);

    const handleChangeStandaloneAmount = useCallback(
        (amount: number | null) => {
            dispatch(changeStandaloneAmount(amount));
        },
        [dispatch]
    );

    const handleChangeStandaloneCurrency = useCallback(
        (newCurrency: Reference | null) => {
            dispatch(changeStandaloneCurrency(newCurrency));
        },
        [dispatch]
    );

    const handleChangeStandaloneDate = useCallback(
        (date: string | null) => {
            dispatch(changeStandaloneDate(date));
        },
        [dispatch]
    );

    return (
        <Root data-qa={qa()}>
            <StyledField title={messages.amountFieldLabel} size='size-11'>
                <NumberEditor
                    min={standaloneConstants.TOTAL_MIN_AMOUNT}
                    max={standaloneConstants.TOTAL_MAX_AMOUNT}
                    value={amount}
                    qa={qa('amount-editor')}
                    allowFloat
                    precision={isCrypto ? 8 : 2}
                    displayFormat='currency'
                    onChange={handleChangeStandaloneAmount}
                    align='right'
                    placeholder={messages.amountPlaceholder}
                    invalid={isInvalidAmount}
                    allowCalculation
                />
            </StyledField>

            <StyledField title={messages.currencyFieldLabel} size='size-11'>
                <RequestCurrenciesDataProvider companyId={companyId} requestId={requestId}>
                    <DropdownEditor
                        qa={qa('currency-editor')}
                        invalid={showValidationErrors && !currency}
                        value={statics.currency.findShortCurrencyByCode(currency)!}
                        onChange={handleChangeStandaloneCurrency}
                        placeholder={messages.standaloneFieldPlaceholder}
                    />
                </RequestCurrenciesDataProvider>
            </StyledField>

            <StyledField title={messages.dateFieldLabel}>
                <DateTimeEditor
                    value={date}
                    onChange={handleChangeStandaloneDate}
                    placeholder={messages.dateFieldPlaceholder}
                    withoutTimePart
                />
            </StyledField>
        </Root>
    );
});

export default StandaloneRequestFieldsSection;
